import { createReducer, on } from '@ngrx/store';
import { AccountWallModels } from '@models/account-wall.model';
import { AccountWallActions } from './account-wall.actions';

export interface AccountWallState {
  allWalls: AccountWallModels.WallWithAssignment[];
  allWallsLoading: boolean;
  queryParams: AccountWallModels.QueryParamFilters;
  latestTs: number | null;
  isLastPage: boolean;
  currentAccountId: string | null;
  isAdding: boolean;
  isRemoving: boolean;
}

const initialState: AccountWallState = {
  allWalls: [],
  allWallsLoading: false,
  queryParams: AccountWallModels.DEFAULT_QUERY_FILTERS,
  latestTs: null,
  isLastPage: false,
  currentAccountId: null,
  isAdding: false,
  isRemoving: false,
};

function sortAssignedFirst(
  walls: AccountWallModels.WallWithAssignment[],
): AccountWallModels.WallWithAssignment[] {
  return [...walls].sort((a, b) => {
    if (a.isAssigned === b.isAssigned) return 0;
    return a.isAssigned ? -1 : 1;
  });
}

function toggleWallAssignment(
  walls: AccountWallModels.WallWithAssignment[],
  wallId: string,
  isAssigned: boolean,
): AccountWallModels.WallWithAssignment[] {
  return sortAssignedFirst(
    walls.map((w) => (w._id === wallId ? { ...w, isAssigned } : w)),
  );
}

export const accountWallReducer = createReducer(
  initialState,
  on(AccountWallActions.getAllWalls, (state, { accountId }) => ({
    ...state,
    allWallsLoading: true,
    currentAccountId: accountId,
    ...(accountId !== state.currentAccountId
      ? { allWalls: [], latestTs: null, isLastPage: false }
      : {}),
  })),
  on(AccountWallActions.getAllWallsSuccess, (state, { walls, isFirstPage }) => {
    const merged = isFirstPage ? walls : [...state.allWalls, ...walls];
    const isLastPage = walls.length < AccountWallModels.PAGE_LIMIT;
    const newLatestTs =
      walls.length > 0 ? walls[walls.length - 1].createdAt : state.latestTs;
    return {
      ...state,
      allWalls: sortAssignedFirst(merged),
      allWallsLoading: false,
      latestTs: newLatestTs,
      isLastPage,
    };
  }),
  on(AccountWallActions.getAllWallsFail, (state) => ({
    ...state,
    allWallsLoading: false,
  })),
  on(AccountWallActions.setSearchQueryFilter, (state, { query }) => ({
    ...state,
    queryParams: { ...state.queryParams, query },
  })),
  on(AccountWallActions.setCameraQueryFilter, (state, { cameraIdsIncludes }) => ({
    ...state,
    queryParams: {
      ...state.queryParams,
      cameraIdsIncludes,
    },
  })),
  on(AccountWallActions.setTagsFilter, (state, { tagIds }) => ({
    ...state,
    queryParams: { ...state.queryParams, tagIds },
  })),
  on(AccountWallActions.removeCameraFilter, (state, { cameraId }) => ({
    ...state,
    queryParams: {
      ...state.queryParams,
      cameraIdsIncludes: state.queryParams.cameraIdsIncludes.filter((id) => id !== cameraId),
    },
  })),
  on(AccountWallActions.removeQueryFilter, (state) => ({
    ...state,
    queryParams: { ...state.queryParams, query: null },
  })),
  on(AccountWallActions.removeTagsFilter, (state) => ({
    ...state,
    queryParams: { ...state.queryParams, tagIds: null },
  })),
  on(AccountWallActions.resetPaging, (state) => ({
    ...state,
    allWalls: [],
    latestTs: null,
    isLastPage: false,
  })),
  on(AccountWallActions.addWallToAccountSuccess, (state, { wallId }) => ({
    ...state,
    isAdding: false,
    allWalls: toggleWallAssignment(state.allWalls, wallId, true),
  })),
  on(AccountWallActions.addWallToAccountFail, (state) => ({
    ...state,
    isAdding: false,
  })),
  on(AccountWallActions.removeWallFromAccountSuccess, (state, { wallId }) => ({
    ...state,
    isRemoving: false,
    allWalls: toggleWallAssignment(state.allWalls, wallId, false),
  })),
  on(AccountWallActions.removeWallFromAccountFail, (state) => ({
    ...state,
    isRemoving: false,
  })),
  on(AccountWallActions.addWallToAccount, (state) => ({
    ...state,
    isAdding: true,
  })),
  on(AccountWallActions.removeWallFromAccount, (state) => ({
    ...state,
    isRemoving: true,
  })),
  on(AccountWallActions.resetState, () => initialState),
);
